import { Injectable } from '@angular/core';
import { IndexedDbService } from 'src/app/shared/services/indexed-db.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { MultimediaItem } from '../models/interfaces';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
  CipiaMultimediaParam,
  IBlobInfo,
  IBlobItem,
  IBlobMetadata,
} from '../models/interfaces';
import { WaitTimeService } from 'src/app/events/services/wait-time.service';
import { Observable } from 'rxjs';
import { EventEmitter, Output } from '@angular/core';
import { timestamp } from 'rxjs/operators';

interface IMultimedias {
  [key: string]: MultimediaItem[];
}
const EVENTS_MULTIMEDIA_KEY = 'multimedia_items';
const tenMbInBytes = 10 * 1024 * 1024; // 10 MB
const ONE_MINUTE = 60000 // un minuto

@Injectable({
  providedIn: 'root',
})
export class MultimediaStoreService {
  public multimediaCipiaItems: IMultimedias = {}; // lista multimedia por cada eventId
  private uuids: string[] = []; // lista de uuids

  private blobItemsIndexedList: IBlobMetadata[] = [];

  private MAX_TIME_BLOB_EXPIRATION : number = ONE_MINUTE * 8
  private isBlobExpirationActive : boolean = true; // activar o desactivar la expiracion de blobs

  private MAX_STORAGE_SIZE: number = tenMbInBytes * 15; // limite del indexedDB
  private currentStorageSize: number = 0; // espacio ocupado actual

  public completedMultimediaCipia: EventEmitter<any> = new EventEmitter<any>();
  public isLoadedMultimediaCipia = false;

  constructor(
    private localStorageService: LocalStorageService,
    private sanitizer: DomSanitizer,
    private waitTimeService: WaitTimeService,
    private indexedDBService: IndexedDbService
  ) {
    if (indexedDBService.isReady) {
      this.initMultimediaCipiaItems();
    } else {
      this.indexedDBService.completed.subscribe(() => {
        this.initMultimediaCipiaItems();
      });
    }
  }

  // logic process (initialized)
  private async initMultimediaCipiaItems(): Promise<void> {
    console.log("multimediaStore : init")
    this.resetStore(); // reestablecemos
    this.fetchAllIndexedBlobsLive(); // obtener blobs actuales (indexedDB in metadata)
  }
  private fetchAllIndexedBlobsLive(): void {
    this.currentStorageSize = 0;
    this.blobItemsIndexedList = [];
  
    this.indexedDBService.getAllBlobsMetadata().subscribe({
      next: (blob) => {
        this.currentStorageSize += blob.size;
        this.blobItemsIndexedList.push(blob);
      },
      error: () => {
        this.blobItemsIndexedList = [];
        this.currentStorageSize = 0;
      },
      complete: async () => {
        console.log('Carga de blobs finalizada');
  
        await this.onBlobsLoadComplete();
      }
    });
  }
  private async onBlobsLoadComplete() : Promise<void> {
    this.sortIndexedBlobsListByTimestamp(); // ordenar por antiguedad los blobs
    
    let uuidEvents: string[] = this.getUuidsFromLocalStorage();
    if (uuidEvents) {
      await Promise.all(
        uuidEvents.map(async (uuid) => {
          let mediaList = await this.fetchAndProcessMultimediaListById(uuid);
          if (mediaList.length == 0) {
            console.log("limpieza de multimedia, multimedia store")
            this.removeMultimediaListByEventIdFromlocalStorage(uuid);
            return;
          }
          this.uuids.push(uuid);
          this.multimediaCipiaItems[uuid] = mediaList;
          this.setMultimediaItemsByEventIdFromLocalStorage(uuid);
        })
      );
      this.updateMultimediaIdsFromLocalStorage();
    }

    this.isLoadedMultimediaCipia = true;
    this.completedMultimediaCipia.emit();
    
    if(this.isBlobExpirationActive){
      this.cleanExpiredBlobs();
    }

    this.showLogs();
  }
  private async fetchAndProcessMultimediaListById(
    multimediaEventId: string
  ): Promise<MultimediaItem[]> {
    let multimediaItems = this.getMultimediaListFromLocalStorage(multimediaEventId);
    if (!multimediaItems || multimediaItems.length <= 0) {return []}

    const processedItems = await Promise.all(
      multimediaItems.map(async (mediaItem: MultimediaItem) => {
        try {
          if(mediaItem.objectUrl){
            //this.revokeBlobUrl(mediaItem.objectUrl)
          }
          mediaItem.url = ''; // Inicializar vacío para urls pasadas
          mediaItem.objectUrl = ""
          mediaItem.completed = false
          mediaItem.loading = false;

          if (!mediaItem.blobId || mediaItem.blobId == "") {
            throw new Error('No tiene asignado un blobId');
          }
          if (!this.isBlobIdInBlobsIndexed(mediaItem.blobId)) {
            throw new Error('El blobId no existe en el indexed');
          }
  
          //const blobItem = await this.getBlobItemFromIndexedDB(mediaItem.blobId);
          const blobUrls = await this.getBlobUrlFromIndexedDB(mediaItem.blobId)
          //mediaItem.url = this.generateUrlFromBlob(blobItem.blob);
          mediaItem.url = blobUrls.safeUrl
          mediaItem.objectUrl = blobUrls.objectUrl // referencia vaciar memoria
          mediaItem.completed = true
          mediaItem.loading = false;
  
          const addevent = this.addEventIdToBlobItemIndexed(mediaItem.blobId, multimediaEventId);
          if(!addevent){
            console.log("multimediaService : no se agrego evento id al item del blob")
          }
          return mediaItem;
        } catch (error) {
          console.error("multimediaService : error en process load,", error)
          // Podrías loguear el error si te interesa saber qué falló
          return null;
        }
      })
    );

    // Filtrar los elementos que no son nulos
    return processedItems.filter((item): item is MultimediaItem => item !== null);
  }
  

  private addUuidToList(uuid: string): void {
    if (!this.uuids.includes(uuid)) {
      this.uuids.push(uuid);
      this.updateMultimediaIdsFromLocalStorage();
    }
  }

  // PUBLIC FUNCIONS
  public updateUrlToMultimediaCipiaItem(
    uuid: string,
    safeUrl: SafeUrl,
    objectUrl: string,
    blobId: string,
    index: number
  ): void {
    // console.log("multimediastore : se modifico la url y blobId")
    this.multimediaCipiaItems[uuid][index].url = safeUrl;
    this.multimediaCipiaItems[uuid][index].objectUrl = objectUrl;
    this.multimediaCipiaItems[uuid][index].blobId = blobId;
    this.addEventIdToBlobItemIndexed(blobId, uuid);
    this.setMultimediaItemsByEventIdFromLocalStorage(uuid);
  }
  public initializeNewMultimediaCipiaItem(uuid: string): void {
    if (!uuid) {
      return;
    }
    this.multimediaCipiaItems[uuid] = []; // inicializa con una lista vacia
    this.setMultimediaItemsByEventIdFromLocalStorage(uuid);
    this.addUuidToList(uuid);
  }
  public addMultimediaCipiaItem(
    uuid: string,
    item: MultimediaItem // recurso a añadir en la lista media (localstorage)
  ) {
    this.multimediaCipiaItems[uuid].push(item);
    this.setMultimediaItemsByEventIdFromLocalStorage(uuid);
  }
  public getLastMultimediaCipiaItemIndex(uuid: string): number {
    return this.multimediaCipiaItems[uuid].length - 1;
  }
  public async getMultimediaUrls(uuid: string) {
    const multimedias = this.multimediaCipiaItems[uuid];
    console.log(multimedias, "obtener urls multimedia", this.multimediaCipiaItems)
    for (const media of multimedias) {
      if (media.blobId) {
        if(media.objectUrl){
          this.revokeBlobUrl(media.objectUrl)
        }
        const { safeUrl, objectUrl } = await this.indexedDBService.getBlobUrl(media.blobId).toPromise();

        media.url = safeUrl;
        media.objectUrl = objectUrl
      }
    }
  }
  public async getMultimediaUrlIndex(uuid: string, index : number) {
    const media = this.multimediaCipiaItems[uuid][index];
    if (media.blobId) {
      if(media.objectUrl){
        this.revokeBlobUrl(media.objectUrl)
      }
      const { safeUrl, objectUrl } = await this.indexedDBService.getBlobUrl(media.blobId).toPromise();

      media.url = safeUrl;
      media.objectUrl = objectUrl
    }
  }
  private revokeMultimediaUrls(uuid : string) {
    const multimedias = this.multimediaCipiaItems[uuid];
    for (const media of multimedias) {
      if(media.objectUrl){
        this.revokeBlobUrl(media.objectUrl)
        media.url = "";
      }
    }
  }
  

  // PUBLIC GETTER
  public getMultimediaByEventIdAndActiveIndex(
    uuid: string,
    index: number
  ): MultimediaItem {
    return this.multimediaCipiaItems[uuid][index];
  }
  public getMultimediasByUuid(uuid: string): MultimediaItem[] {
    return this.multimediaCipiaItems[uuid] || [];
  }

  // PUBLIC LOGIC
  public hasUuid_eventInMultimediaItems(uuid: string): boolean {
    return this.multimediaCipiaItems.hasOwnProperty(uuid);
  }
  public hasItemsForUuid(uuid: string): boolean {
    const items = this.multimediaCipiaItems[uuid];
    return Array.isArray(items) && items.length > 0;
  }

  // PUBLIC ASYNC
  public async saveBlob(
    blob: Blob,
    type: string,
    multimediaItemWaitTime: CipiaMultimediaParam
  ): Promise<{ id: string; url: string }> {
    try {
      if (this.isStorageExceededWithAddition(blob.size)) {
        // console.log("multimediastorage : se tiene que limpiar el inedexed ",this.currentStorageSize, blob.size)
        await this.freeUpSpaceForNewBlob(blob.size);
        // console.log("multimediastorage : se limpio lo suficiente ", this.currentStorageSize ,blob.size)
      }

      const { id, url, timestamp } = await this.saveBlobToIndexedDb(
        blob,
        type,
        multimediaItemWaitTime
      );

      console.log("multimediastore : se añadio el blob a la lista")
      this.currentStorageSize += blob.size;
      this.blobItemsIndexedList.unshift({
        id,
        size: blob.size,
        type: blob.type,
        timestamp,
        eventIds: [],
      });
      //this.showLogs();
      return { id, url };
    } catch (error) {
      throw new Error(`Error al guardar el blob: ${error}`);
    }
  }
  public async clearMultimediaStorage(): Promise<void> {
    for (const event of this.uuids) {
      let auxMultimediaCipiaItems: MultimediaItem[] =
        this.multimediaCipiaItems[event];
      if (!auxMultimediaCipiaItems) {
        return;
      }
      for (const item of auxMultimediaCipiaItems) {
        if (item.blobId) {
          await this.deleleteBlobByIdFromIndexedDb(item.blobId);
        }
      }
    }
  }
  public async clearAllMultimediaBlobFromIndexedDB() {
    await this.indexedDBService.deleteAllBlob().toPromise();
  }
  // reset localStorage and IndexedDb
  public async clearmMultimediaStorage(): Promise<boolean> {
    try {
      this.uuids.forEach((eventId) => {
        // eliminamos cada lista multimedia de cada evento
        this.removeMultimediaListByEventIdFromlocalStorage(eventId);
      });
      this.deleteAllBlobsFromIndexedDb(); // vaciamos el indexedDB
      this.resetStore(); // vaciamos todos los datos
      this.updateMultimediaIdsFromLocalStorage();
      return true;
    } catch {
      return false;
    }
  }

  // blobItemsIndexedList control function
  private addEventIdToBlobItemIndexed(
    blobId: string,
    eventId: string
  ): boolean {
    try {
      const index = this.blobItemsIndexedList.findIndex(
        (blob) => blob.id === blobId
      );
      if (index !== -1) {
        this.blobItemsIndexedList[index].eventIds.push(eventId);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }
  private removeMultimediaCipiaItemByBlobIdAndEventId(
    blobId: string,
    eventId: string
  ): boolean {
    console.log("multimdiaStore: se removio multimedia (init)")
    try {
      const items = this.multimediaCipiaItems[eventId];
      if (!items) return false;
  
      const index = items.findIndex(item => item.blobId === blobId);
      if (index === -1) return false;
      const removedItem = items[index]; // Almacenar el ítem antes de eliminarlo
      if(removedItem.objectUrl){
        this.revokeBlobUrl(removedItem.objectUrl)
      }

      items.splice(index, 1);

      this.setMultimediaItemsByEventIdFromLocalStorage(eventId);
  
      if (items.length == 0) {
        this.removeMultimediaListByEventIdFromlocalStorage(eventId);
        this.removeIdFromMultimediaEventIds(eventId);
        delete this.multimediaCipiaItems[eventId];
      }
  
      return true;
    } catch (error) {
      // Podrías loguear el error si lo necesitas
      console.error("Error al remover blob de cipia", error);
      return false;
    }
  }
  
  // blobItemsIndexedList control function (indexeddb)
  private sortIndexedBlobsListByTimestamp(): void {
    this.blobItemsIndexedList.sort((a, b) => {
      return b.timestamp - a.timestamp; // Orden descendente (más recientes primero)
    });
  }

  private removeIdFromMultimediaEventIds(eventId: string): void {
    this.uuids = this.uuids.filter(
      (multimediaEventId) => multimediaEventId !== eventId
    );
    this.updateMultimediaIdsFromLocalStorage();
  }

  // control store
  private isBlobIdInBlobsIndexed(blobId: string): boolean {
    return this.blobItemsIndexedList.some(
      (blobMetadata) => blobMetadata.id === blobId
    );
  }
  private isStorageExceededWithAddition(additionalSize: number): boolean {
    return this.currentStorageSize + additionalSize > this.MAX_STORAGE_SIZE;
  }
  private async freeUpSpaceForNewBlob(newBlobSize: number): Promise<void> {
    this.sortIndexedBlobsListByTimestamp();

    if (newBlobSize >= this.MAX_STORAGE_SIZE) {
      console.error('El blob supero el limite del store');
      return;
    }

    console.log("multimedia store : liberar espacio de blob")
    for (let i = this.blobItemsIndexedList.length - 1; i >= 0; i--) {
      const blobData = this.blobItemsIndexedList[i];
      if(blobData){
        if (this.currentStorageSize + newBlobSize > this.MAX_STORAGE_SIZE) {
          try {
            const isEliminated = await this.deleteBlob(blobData);

            console.log("multimediaStore: se eliminó un blob para limpieza", this.currentStorageSize + newBlobSize);
            // console.log("multimediaStore : se elimino un blob para limpieza", this.currentStorageSize+newBlobSize)
          } catch (error) {
            console.error(
              `Error al eliminar el blob con ID ${blobData.id}:`,
              error
            );
            break; // Salir si ocurre un error al eliminar un blob
          }
        } else {
          return;
        }
      }
    }
  }

  private async clearOldBlobsToFitNewWhile(newBlobSize: number): Promise<void> {
    this.sortIndexedBlobsListByTimestamp();

    if (newBlobSize >= this.MAX_STORAGE_SIZE) {
      console.error('El blob supero el limite del store');
      return;
    }

    while (
      this.currentStorageSize + newBlobSize > this.MAX_STORAGE_SIZE &&
      this.blobItemsIndexedList.length > 0
    ) {
      // Obtén el último elemento del array
      const blobData =
        this.blobItemsIndexedList[this.blobItemsIndexedList.length - 1];

      if (blobData) {
        try {
          const isEliminated = await this.deleteBlob(blobData);
          if (isEliminated) {
            console.log("multimediaStore: se eliminó un blob para limpieza", this.currentStorageSize + newBlobSize);
          } else {
            return;
          }
        } catch (error) {
          console.error(
            `Error al eliminar el blob con ID ${blobData.id}:`,
            error
          );
          return;
        }
      } else {
        return;
      }
    }
  }

  private async deleteBlob(blobData: IBlobMetadata): Promise<boolean> {
    try {
      await this.indexedDBService.deleteBlob(blobData.id).toPromise();
      if(blobData.eventIds && blobData.eventIds.length > 0){
        blobData.eventIds.map((eventId) => {
          this.removeMultimediaCipiaItemByBlobIdAndEventId(blobData.id, eventId)
        });
      }
      this.currentStorageSize -= blobData.size;
      this.blobItemsIndexedList.pop();
      return true;
    } catch (error) {
      console.error(`Error al eliminar el blob con ID ${blobData.id}:`, error);
      return false;
    }
  }

  // indexedDB functions control
  private async saveBlobToIndexedDb(
    blob: Blob,
    type: string,
    multimediaItemWaitTime: CipiaMultimediaParam
  ): Promise<{ id: string; url: string; timestamp: number }> {
    // console.log("multimediastore : se intenta guardar el blob en el indexedDB")

    try {
      const arrayBuffer = await this.convertBlobToArrayBuffer(blob);
      const nowTime = new Date().getTime(); // pass to saveBlob
      const id = await this.indexedDBService
        .saveBlob(arrayBuffer, type, nowTime)
        .toPromise();

      const blobUrl = URL.createObjectURL(blob);
      this.waitTimeService.stopTimeOfCipiaByIds(
        multimediaItemWaitTime.from!,
        multimediaItemWaitTime
      );

      return { id: id, url: blobUrl, timestamp: nowTime };
    } catch (error) {
      throw new Error(`Error al guardar el blob en IndexedDB: ${error}`);
    }
  }
  private async deleleteBlobByIdFromIndexedDb(
    blobId: string
  ): Promise<boolean> {
    try {
      await this.indexedDBService.deleteBlob(blobId).toPromise;
      return true;
    } catch {
      return false;
    }
  }
  private async deleteAllBlobsFromIndexedDb(): Promise<boolean> {
    try {
      await this.indexedDBService.deleteAllBlob().toPromise();
      return true;
    } catch {
      return false;
    }
  }
  // localStorage functions control
  private getUuidsFromLocalStorage(): string[] {
    let uuidEvents = this.localStorageService.getItem(
      EVENTS_MULTIMEDIA_KEY
    ) as string[];
    if (uuidEvents) {
      uuidEvents = uuidEvents.filter((uuid) => uuid !== null);
      return uuidEvents;
    }
    return [];
  }
  private updateMultimediaIdsFromLocalStorage() {
    this.localStorageService.setItem(EVENTS_MULTIMEDIA_KEY, this.uuids);
  }
  private setMultimediaItemsByEventIdFromLocalStorage(eventId: string) {
    if (eventId) {
      this.localStorageService.setItem(
        eventId,
        this.multimediaCipiaItems[eventId]
      );
    }
  }
  private removeMultimediaListByEventIdFromlocalStorage(eventId: string) {
    this.localStorageService.removeItem(eventId);
  }
  private getMultimediaListFromLocalStorage(
    multimediaEventId: string
  ): MultimediaItem[] | null {
    return this.localStorageService.getItem(multimediaEventId) as
      | MultimediaItem[]
      | null;
  }

  // utilidades
  public showLogs(): void {
    console.log(
      'multimediaStorage : almacenamiento ',
      (this.currentStorageSize / 1073741824).toFixed(2),
      'de ',
      (this.MAX_STORAGE_SIZE / 1073741824).toFixed(2) // Convierte MAX_STORAGE_SIZE a GB y lo redondea a 2 decimales
    );
    console.log(
      'multimediaStorage : multimedia cipia items ',
      this.multimediaCipiaItems
    );
    console.log(
      'multimediaStorage : lista blobs indexed ',
      this.blobItemsIndexedList
    );
    console.log('multimediaStorage : multimedias por evento ', this.uuids);
  }
  private resetStore(): void {
    this.multimediaCipiaItems = {};
    this.uuids = [];
    this.blobItemsIndexedList = [];
    this.isLoadedMultimediaCipia = false;
  }
  private async getBlobItemFromIndexedDB(blobId: string): Promise<IBlobItem> {
    return await this.indexedDBService.getBlobItem(blobId).toPromise();
  }
  private async getBlobUrlFromIndexedDB(blobId : string): Promise<{ safeUrl: SafeUrl, objectUrl: string }>{
    return await this.indexedDBService.getBlobUrl(blobId).toPromise();
  }
  private convertBlobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        resolve(event.target.result as ArrayBuffer);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsArrayBuffer(blob);
    });
  }
  private generateUrlFromBlob(blob: Blob): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
  }

  // deprecated funcstions
  public addMultimediaItemToMultimediaEvent(
    eventId: string,
    multimediaItem: MultimediaItem
  ) {
    // console.log("multimediastore : se añadio un multimedia Item al cipia Video")
    this.multimediaCipiaItems[eventId].push(multimediaItem);
    this.setMultimediaItemsByEventIdFromLocalStorage(eventId);
  }
  private isStorageExceeded(): boolean {
    return this.currentStorageSize > this.MAX_STORAGE_SIZE;
  }
  private revokeBlobUrl(objectUrl: string): void {
    console.log("revocar url multimedia")
    URL.revokeObjectURL(objectUrl);
  }

  public async deleteMultimediaByEventId(uuid: string): Promise<void> {
    const eventMediaItems = this.multimediaCipiaItems[uuid];
    if (!eventMediaItems) {
      return;
    }
    try {
      await Promise.all(
        eventMediaItems.map(async (mediaEvent) => {
          if (mediaEvent.blobId) {
            const blobItem = this.blobItemsIndexedList.find(
              (item) => item.id === mediaEvent.blobId
            );
            if (blobItem) {
              await this.deleteBlob(blobItem);
            }
          }
        })
      );
    } catch (error) {
      throw error;
    }
  }
  private async cleanExpiredBlobs() {
    setTimeout(async () => { 
      const currentTime = Date.now();
      const expirationTime = currentTime - this.MAX_TIME_BLOB_EXPIRATION;
  
      const now = new Date();
      const formattedNow = now.toISOString().replace('T', ' ').substring(0, 19);
      console.log("multimediaStore expired: blobs expirados, limpiar", formattedNow);

      // Aquí iteramos sobre la lista de blobs
      for (const blobItem of this.blobItemsIndexedList) {
        const blobTimestamp = blobItem.timestamp;
        if (blobTimestamp < expirationTime) {
          console.log("multimediaStore expired: blob expirado encontrado");
          
          // Esperamos la eliminación del blob
          const isEliminated = await this.deleteBlob(blobItem);
          if (isEliminated) {
            console.log("multimediaStore: se eliminó un blob para limpieza", this.currentStorageSize);
          }
        }
      }

      if (this.isBlobExpirationActive) {
        this.cleanExpiredBlobs();
      }
    }, this.MAX_TIME_BLOB_EXPIRATION);
  }
  
}
